<script context="module" lang="ts">
	function getSelected(
		slug: string,
		menuItems: any[],
		menuItemsIsSortedForSearch = false
	): any[] {
		let ret = [];

		const items = menuItemsIsSortedForSearch
			? menuItems
			: sortItems(menuItems);

		for (const item of items) {
			if (!item) {
				continue;
			}

			if (slug.indexOf(item.url) === 0) {
				ret.push(item);

				if (item.childs) {
					for (const child of item.childs) {
						if (!child) {
							continue;
						}

						if (slug.indexOf(child.url) === 0 && child.color) {
							ret.push(child);
							break;
						}
					}
				}

				break;
			}
		}

		return ret;
	}

	export function slugToColor(
		forSlug: string,
		menuItems: any[],
		ret: "hex" | "rgba" = "hex",
		fallbackColor: string = "#7b8157"
	) {
		try {
			if (!forSlug || !menuItems || menuItems.length === 0) {
				return fallbackColor;
			}

			let color = fallbackColor;

			const x = getSelected(
				forSlug.indexOf("/") === 0 ? forSlug : `/${forSlug}`,
				menuItems
			);

			for (const item of x) {
				if (item.color) {
					color = item.color;
				}
			}

			return ret === "hex" ? color : convertHexToRGBA(color);
		} catch (e) {
			console.error(e, menuItems);
		}
	}

	export const convertHexToRGBA = (hexCode: string, opacity: number = 1) => {
		let hex = hexCode.replace("#", "");

		if (hex.length === 3) {
			hex = `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}`;
		}

		const r = parseInt(hex.substring(0, 2), 16);
		const g = parseInt(hex.substring(2, 4), 16);
		const b = parseInt(hex.substring(4, 6), 16);

		if (opacity > 1 && opacity <= 100) {
			opacity = opacity / 100;
		}

		return `rgba(${r},${g},${b},${opacity})`;
	};

	export function hasSubmenu(path: string, menuItems: any[]): any {
		if (!menuItems || menuItems.length === 0) {
			return false;
		}

		for (const item of menuItems) {
			if (path.indexOf(item.url) === 0) {
				return item.childs;
			}
		}
	}

	export function sortItems(items: any[]): any[] {
		if (!items || items.length === 0) {
			return [];
		}

		const ret = [...items].sort(
			(a: any, b: any) => b.url?.length - a.url?.length
		);

		return ret.map((item: any) => {
			if (item.childs) {
				item.childs = item.childs.sort(
					(a: any, b: any) => b.url?.length - a.url?.length
				);
			}
			return item;
		});
	}
</script>

<script lang="ts">
	import { onMount, getContext } from "svelte";
	import Hydrator from "@reperio-news/core.components/Hydrator.svelte";
	import { fade } from "svelte/transition";

	// @ts-ignore
	import searchIcon from "./search.svg";
	// @ts-ignore
	import menuIcon from "./menuIcon.svg";
	// @ts-ignore
	import chevronDown from "./chevron-down.svg";
	// @ts-ignore
	import closeIcon from "./closeIcon.svg";

	export let id: string;
	export let globalId: string;
	export let globalSource: number;
	// ignore no use
	id || globalId || globalSource;

	// simplified context
	export let context: any;
	const c: SvelteStore<any> = getContext("context");

	if (!context) {
		context = $c;
	}

	export let data = {
		items: [],
	};

	let scrollY: number;
	let menuTop = 0;
	let menuVisible = false;
	let dropdownTop = 0;
	let dropdownLeft = 0;
	let dropdownHeight = 0;
	let menuDom: HTMLDivElement;
	let menuItemsDom: HTMLDivElement;
	let dropdownDom: HTMLDivElement;
	let showDropdown = false;
	let containerDom: HTMLDivElement;
	let menuOverlayDom: HTMLDivElement;
	let searchFormDom: HTMLFormElement;
	let showSearch = false;
	let fakeDom: HTMLDivElement;

	//	positions go through state + style: directives, never dom.style writes:
	//	in Svelte 5 legacy mode `dropdownDom.style.top = ...` counts as a
	//	mutation of the bound var and re-triggers every $: block reading it
	$: if (showDropdown && containerDom && dropdownDom && menuOverlayDom) {
		let x = containerDom.getBoundingClientRect();

		dropdownTop = x.top + x.height;
		dropdownLeft = Math.max(
			x.left + x.width - dropdownDom.offsetWidth,
			0
		);
		dropdownHeight = window.innerHeight - dropdownTop - 35;
	}

	$: if (showSearch && searchFormDom) {
		searchFormDom.querySelector("input").focus();
	}

	function onScroll(_scrollY: number) {
		if (fakeDom && scrollY) {
			menuTop = Math.max(fakeDom.getBoundingClientRect().top, 0);
		}
	}

	$: onScroll(scrollY);

	let color = context.domain_config.MISC.menuBorderColor;

	let sortedItems: any;
	let selected: any;
	let items: any[];

	$: if (data && data.items && context) {
		sortedItems = sortItems(data.items);

		items = data.items;
		if (context.path !== "/") {
			selected = getSelected(context.path, sortedItems, true)?.pop();
			if (selected) {
				items = items.filter(
					(item) => item && item.url !== selected.url
				);
				selected.selected = true;
				items.unshift(selected);
			}
		}

		if (context.path !== "/") {
			color = slugToColor(
				context.path,
				sortedItems,
				"hex",
				context.domain_config.MISC.menuBorderColor
			);
		}
	}

	function toggleSubItems(e: MouseEvent) {
		const parent = (<HTMLElement>e.target).parentNode
			.parentNode as HTMLLIElement;
		parent.classList.toggle("collapsed");
	}

	let isMobile = false;
	let menuIsOverflowing = false;
	let mounted = false;

	function checkOverflow(m: boolean, _dom?: any) {
		//	menuItemsDom is null mid-flight while Hydrator swaps its {#if}
		//	branches after mount (the swap tears down bind:this before
		//	re-binding) — same failure as the adevarul Menu
		if (!m || !menuItemsDom) {
			return;
		}

		const itemsWidth = [...menuItemsDom.querySelectorAll("a")]
			.map((item) => item.offsetWidth)
			.reduce((a, b) => a + b + 21, 0);

		if (itemsWidth > menuItemsDom.offsetWidth) {
			menuIsOverflowing = true;
		}
	}

	//	menuItemsDom in the statement so it re-runs once Hydrator's
	//	post-mount branch swap re-binds the container
	$: checkOverflow(mounted && !!menuItemsDom, menuItemsDom);

	// cls related bs — reactive on the doms so it re-runs (and the menu
	// still un-hides) if the Hydrator swap re-binds them after mount.
	// clamped to 0 so a mount that happens after the user already scrolled
	// doesn't place the fixed menu above the viewport
	$: if (mounted && menuDom && fakeDom) {
		setTimeout(() => {
			if (!fakeDom) {
				return;
			}
			menuTop = Math.max(fakeDom.getBoundingClientRect().top, 0);
			menuVisible = true;
		}, 1);
	}

	onMount(() => {
		isMobile =
			/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
				navigator.userAgent
			);

		mounted = true;
	});
</script>

<Hydrator props={$$props} component="TopMenu" hydrate={true}>
	<div class="fake" bind:this={fakeDom} />

	<div
		bind:this={menuDom}
		class="menu"
		class:admin={__ADMIN__}
		style:display={menuVisible ? "flex" : "none"}
		style:top="{menuTop}px"
		style="--bgColor: {context.domain_config.MISC
			.menuDefaultcolor}; --menuBorderColor: {color};"
	>
		<div
			bind:this={containerDom}
			class="container"
			style=" --menuFontColor: {context.domain_config.MISC
				.menuFontColor}; --ratio: {context.domain_config.MISC.logo
				.width / context.domain_config.MISC.logo.height};"
		>
			<a
				href="/"
				title={context.domain_config.default.friendlyName}
				class="logo"
			>
				<img
					src="/logo.png"
					alt={context.domain_config.default.friendlyName}
					width={context.domain_config.MISC.logo.width}
					height={context.domain_config.MISC.logo.height}
				/>
			</a>

			{#if showSearch}
				<form
					bind:this={searchFormDom}
					method="get"
					action="/search"
					autocomplete="off"
				>
					<input
						type="search"
						name="q"
						placeholder="Caută în {context.domain_config.default
							.friendlyName}"
					/>
				</form>
			{:else}
				<div class="items" bind:this={menuItemsDom}>
					{#if items}
						{#if !isMobile}
							<a href="/">Home</a>
						{/if}

						{#each items as item}
							<a
								class:selected={item.selected}
								href={item.url}
								target={item.target}
								style="--color:{item.color ||
									context.domain_config.MISC
										.menuBorderColor};">{item.name}</a
							>
						{/each}
					{/if}
				</div>
			{/if}

			{#if !isMobile && menuIsOverflowing && !showSearch}
				<div
					class="menuButton"
					style="margin-left: auto"
					on:click={() => (showDropdown = !showDropdown)}
				>
					...
				</div>
			{/if}

			<div
				class="menuButton"
				on:click={() => {
					if (!showSearch) {
						showSearch = true;
					} else {
						searchFormDom.submit();
					}
				}}
			>
				<img
					class="menuButton"
					src={searchIcon}
					alt="search"
					width="32"
					height="32"
				/>
			</div>

			{#if !showSearch}
				<div
					class="menuButton"
					class:menuButtonSelected={showDropdown}
					on:click={() => (showDropdown = !showDropdown)}
				>
					<img
						class="menuButton"
						src={menuIcon}
						alt="menu"
						width="32"
						height="32"
					/>
				</div>
			{:else}
				<div class="menuButton">
					<img
						src={closeIcon}
						alt="Închide"
						width="32"
						height="32"
						on:click={() => (showSearch = false)}
					/>
				</div>
			{/if}
		</div>

		<div class="other">
			<a
				href="https://www.facebook.com/HistoriaMagazin"
				target="_blank"
				title="Historia - Facebook"
				class="facebook"
			>
				<svg
					role="img"
					viewBox="0 0 24 24"
					xmlns="http://www.w3.org/2000/svg"
					><title>Facebook</title><path
						d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
					/></svg
				>
			</a>

			<a
				href="https://twitter.com/HistoriaMagazin"
				target="_blank"
				title="Historia.ro - Twitter"
				class="twitter"
			>
				<svg
					role="img"
					viewBox="0 0 24 24"
					xmlns="http://www.w3.org/2000/svg"
					><title>Twitter</title><path
						d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"
					/></svg
				>
			</a>

			<a
				href="https://www.youtube.com/channel/UCHOZorJ4dMpBEFbZjgZ9b-g"
				target="_blank"
				title="Historia - Youtube"
				class="youtube"
			>
				<svg
					role="img"
					viewBox="0 0 24 24"
					xmlns="http://www.w3.org/2000/svg"
					><title>YouTube</title><path
						d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
					/></svg
				>
			</a>

			<a
				href="https://www.instagram.com/historia.romania"
				target="_blank"
				title="Historia - Instagram"
				class="instagram"
			>
				<svg
					role="img"
					viewBox="0 0 24 24"
					xmlns="http://www.w3.org/2000/svg"
					><title>Instagram</title><path
						d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z"
					/></svg
				>
			</a>
		</div>
	</div>

	{#if showDropdown}
		<div
			transition:fade={{ duration: 100 }}
			bind:this={dropdownDom}
			class="menuDropdown"
			style:top="{dropdownTop}px"
			style:left="{dropdownLeft}px"
			style:height="{dropdownHeight}px"
			style="--bgColor: {context.domain_config.MISC.menuDefaultcolor}"
		>
			{#if data && data.items}
				<ul>
					{#each data.items as item}
						<li
							class="mainItem collapsed"
							style="--color: {item.color}"
						>
							<div>
								<a href={item.url} target={item.target}
									>{item.name}</a
								>
								{#if item.childs && item.childs.length !== 0}
									<img
										class="toggleSub"
										src={chevronDown}
										width="32"
										height="32"
										alt="Mai multe"
										on:click={toggleSubItems}
									/>
								{/if}
							</div>
							{#if item.childs && item.childs.length !== 0}
								<ul>
									{#each item.childs as subItem}
										<li
											class="subItem"
											style="--color: {item.color}"
										>
											<a
												href={subItem.url}
												target={subItem.target}
											>
												{subItem.name}
											</a>
										</li>
									{/each}
								</ul>
							{/if}
						</li>
					{/each}
				</ul>
			{/if}
		</div>

		<div
			class="menuOverlay"
			bind:this={menuOverlayDom}
			style:top="{dropdownTop}px"
			on:click={() => (showDropdown = false)}
		/>
	{/if}
</Hydrator>

<svelte:window bind:scrollY />

<svelte:head>
	<meta name="theme-color" content={color} />
</svelte:head>

<style lang="scss">
	.admin {
		position: relative !important;
	}

	.fake {
		height: 80px;
	}

	.menu {
		display: none;
		user-select: none;
		position: fixed;
		left: 0px;
		top: 0px;
		z-index: 5;
		width: calc(100% - 10px);
		margin: auto auto 10px auto;
		background-color: var(--bgColor);
		padding: 5px 5px 0 5px;
		overflow: hidden;
		border-bottom: 1px solid var(--menuBorderColor);

		.other {
			display: flex;
			justify-content: space-between;
			align-items: center;
			flex-direction: row;
			flex-wrap: wrap;
			padding: 10px 10px 10px 20px;
			min-width: 200px;
			opacity: 0.45;

			a {
				width: 24px;
				height: 24px;
				margin: 5px;
			}

			a.facebook:hover {
				fill: #4064ad;
				transition: all 0.1s ease-in-out;
			}

			a.twitter:hover {
				fill: #209beb;
				transition: all 0.1s ease-in-out;
			}

			a.youtube:hover {
				fill: #f20000;
				transition: all 0.1s ease-in-out;
			}

			a.instagram:hover {
				fill: #7136bd;
				transition: all 0.1s ease-in-out;
			}
		}

		.container {
			width: 100%;
			margin: auto;
			height: 60px;
			display: flex;
			flex-direction: row;
			flex-wrap: nowrap;
			overflow: hidden;
			align-items: center;

			a {
				margin-right: 10px;
				height: 50px;
				display: flex;
				justify-content: center;
				align-items: center;
				font-style: normal;
				font-weight: 600;
				font-size: 16px;
				color: var(--menuFontColor);
				padding: 0 5px 0 5px;
				border-bottom: 3px solid var(--bgColor);
				transition: all 350ms;
			}

			a.selected {
				font-weight: bold;
			}

			a:hover {
				border-bottom: 3px solid var(--color) !important;
				margin-top: -5px;
				transition: margin 350ms;
			}

			a.logo {
				height: 25px;
				width: calc(25px * var(--ratio));

				img {
					object-fit: contain;
					width: 100%;
					height: 100%;
				}
			}

			a.logo:hover {
				border: none !important;
			}

			.menuButton {
				letter-spacing: 3px;
				margin-left: 15px;
				font-style: normal;
				font-weight: 600;
				font-size: 16px;
				color: var(--menuFontColor);
				min-width: 32px;
				min-height: 32px;
				display: flex;
				align-items: center;
				cursor: pointer;
				opacity: 0.9;

				:hover {
					opacity: 1;
				}

				img {
					margin-left: auto;
				}
			}

			.menuButtonSelected {
				background-color: rgb(234, 240, 222);
				border-radius: 5px;
			}

			.items {
				display: flex;
				flex-direction: row;
				flex-wrap: wrap;
				align-items: center;
				justify-content: center;
				overflow: hidden;
				height: 53px;
				--rmx: calc(var(--ratio) * 50px + 80px);
				width: calc(100% - var(--rmx));
			}

			form {
				display: flex;
				flex-direction: row;
				flex-wrap: nowrap;
				overflow: hidden;
				height: 53px;
				--rmx: calc(var(--ratio) * 50px + 60px);
				width: calc(100% - var(--rmx));

				input {
					background-color: #eeeeee;
					width: calc(100% - 20px);
					height: 90%;
					border: none;
					text-align: center;
					border-radius: 5px;
					outline-width: 0;
					outline: none;
					font-size: 16px;
				}
			}
		}
	}

	.menuDropdown {
		position: fixed;
		width: 400px;
		padding: 10px;
		min-height: 300px;
		background-color: var(--bgColor);
		z-index: 10;
		display: flex;
		flex-direction: column;
		flex-wrap: nowrap;
		overflow-y: scroll;

		ul {
			display: flex;
			flex-direction: column;
			flex-wrap: wrap;
			width: calc(100% - 20px);
			margin: 0;
			padding: 10px;

			a {
				height: 32px;
				line-height: 32px;
			}

			li:hover {
				background-color: #eee;
				transition: background-color 350ms;
			}

			li {
				list-style: none;
				margin: 0;
				padding: 0;
				display: flex;
				justify-content: center;
				background-color: rgb(255, 255, 255);
				margin-bottom: 5px;
				border-radius: 0px 5px 5px 0;
				border-left: 2px solid var(--color);
				border-bottom: 1px solid #eee;

				width: calc(100% - 10px);
				flex-direction: column;
				padding: 5px;

				ul {
					opacity: 1;
					transition: opacity 0.5s;
					li {
						border-bottom: 1px solid #eee;
						border-radius: none;
					}

					li:hover {
						background-color: rgb(226, 219, 219);
						transition: background-color 350ms;
					}
				}

				div {
					width: 100%;
					display: flex;
					justify-content: space-between;
					align-items: center;

					a {
						width: calc(100% - 50px);
					}

					.toggleSub {
						transform: rotate(180deg);
						transition: transform 350ms;
						cursor: pointer;
					}
				}
			}

			li.collapsed {
				div {
					.toggleSub {
						transform: rotate(0deg);
						transition: transform 350ms;
					}
				}

				ul {
					display: none;
					opacity: 0.1;
				}
			}
		}
	}

	.menuOverlay {
		width: 100%;
		height: 100%;
		z-index: 9;
		position: fixed;
		left: 0px;
		top: 0px;
		background-color: #111;
		opacity: 0.85;
	}

	@media screen and (max-width: 500px) {
		.menuDropdown {
			width: calc(100% - 20px);
		}
	}

	@media screen and (max-width: 1280px) {
		.menu {
			.container {
				.items {
					width: 100%;
				}

				form {
					width: 100%;
				}
			}

			.other {
				display: none;
			}
		}
	}
</style>
